import Step from "./Step"
import { useState, useRef } from "react"
import Button from "../../Components/Button"
import { Field } from "formik"
import * as Yup from "yup"
import FormikField from "../../Components/Forms/FormikField"
import BlessingsPreview from "../../Components/BlessingsPreview"
import Recorder from "../../Components/Recorder"
import Modal from "../../Components/Modal"
import { useAtom } from "jotai"
import { planAtom, planTokenAtom, roleAtom, userAtom } from "../../state"
import api from "../../services/api"
import GoalBlock from "../../Components/GoalBlock"
import { getStepMetadata } from "../../constants/stepRegistry"
import { PLAN_USER_ROLE } from "../../constants/planUserRole"
import { BLESSINGS_RESOURCE_URL } from "../../constants/resources"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"
import { sentryService } from "../../services/sentry"

const stepMetadata = getStepMetadata("blessings")

const shape = {
  blessing: Yup.string().required("Required"),
}

const schema = Yup.object().shape(shape)

const step = {
  name: stepMetadata.name,
  title: "Speak life into your student.",
  type: stepMetadata.type,
  instructions: [
    "Share a blessing with your student as they step into the year ahead. Use this space to affirm who they are, call out who they're becoming, and invite God's presence into the year ahead.",
    "Take a moment reflect and then share your blessing by writing or recording it below.",
    <MoreInfoVideoModal
      src={BLESSINGS_RESOURCE_URL}
      postText={"How to give a blessing"}
      title={"Blessing Examples"}
      text={""}
      textClassName={"text-blessings no-underline"}
    />,
  ],
  discussionQuestions: [
    "What do I see in this student that reflects God's character?",
    "What do I hope God does in their life this year?",
    "What truth or scripture could anchor them in times of doubt or struggle?",
  ],
}

function Blessings() {
  const [user] = useAtom(userAtom)
  const [role] = useAtom(roleAtom)
  const [plan] = useAtom(planAtom)
  const [activeType, setActiveType] = useState(null)
  const [isAudioOpen, setIsAudioOpen] = useState(false)
  const [recorderKey, setRecorderKey] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [planToken] = useAtom(planTokenAtom)
  const fileInputRef = useRef(null)

  const getPreviewData = (data, filterMentorId = null) => {
    if (filterMentorId) {
      return {
        audio_blessings: data.audio_blessings.filter(item => item.user_id === filterMentorId),
        text_blessings: data.text_blessings.filter(item => item.user_id === filterMentorId),
      }
    }
    return data
  }
  const getIsComplete = data => {
    if (role == PLAN_USER_ROLE.YOUTH || role == PLAN_USER_ROLE.GUIDE) {
      return data.audio_blessings.length > 0 || data.text_blessings.length > 0
    }
    return (
      data.audio_blessings.filter(item => item.user_id == user.id).length > 0 ||
      data.text_blessings.filter(item => item.user_id == user.id).length > 0
    )
  }

  const downloadBlob = (blob) => {
    const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('ogg') ? 'ogg' : 'wav'
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `blessing-recording.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  const onAudioComplete = (blob, refreshData) => {
    setIsUploading(true)
    api.createMedia(blob, planToken).then(response => {
      api
        .createPlanContent(
          {
            reset: 0,
            content: [
              {
                key: "audio_blessings",
                value: response.data.id,
                plan_token: planToken,
              },
            ],
          },
          planToken,
        )
        .then(() => {
          setIsUploading(false)
          setIsAudioOpen(false)
          refreshData()
        })
        .catch((e) => {
          sentryService.captureException(e)
          setIsUploading(false)
          downloadBlob(blob)
          alert("Your blessing couldn't be saved. Your recording has been downloaded to your device. You can re-upload it using the 'Upload Audio Blessing' button.")
        })
    }).catch((e) => {
      sentryService.captureException(e)
      setIsUploading(false)
      downloadBlob(blob)
      alert("Your recording couldn't be uploaded. Your recording has been downloaded to your device. You can re-upload it using the 'Upload Audio Blessing' button.")
    })
  }
  const onFileUpload = (e, refreshData) => {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!['wav', 'webm', 'ogg', 'mp3', 'm4a', 'aac'].includes(ext)) {
      alert("Please select a valid audio file (.wav, .mp3, .m4a, .aac, .webm, or .ogg)")
      return
    }
    if (file.size > 150 * 1024 * 1024) {
      alert("File is too large. Maximum file size is 150 MB.")
      return
    }
    onAudioComplete(file, refreshData)
  }

  const onSubmit = (values, actions, onSubmitIn) => {
    onSubmitIn(
      { [activeType == "audio" ? "audio_blessings" : "text_blessings"]: [values.blessing] },
      false,
      false,
    )
    actions.resetForm()
    setActiveType(null)
  }
  return (
    <Step
      step={step}
      Preview={BlessingsPreview}
      getIsComplete={getIsComplete}
      getPreviewData={getPreviewData}
      keys={[
        "audio_blessings",
        "text_blessings",
        "visions",
        "other_title",
        "faith_vision",
        "family_vision",
        "sports_vision",
        "school_vision",
        "arts_music_vision",
        "travel_vision",
        "hobbies_vision",
        "service_vision",
        "other_vision",
        "relationships_vision",
        "work_vision",
        "justice_vision",
      ]}
      onSubmit={onSubmit}
      schema={schema}
      image={stepMetadata.image}
      theme={stepMetadata.theme}
      description={stepMetadata.description}
    >
      {(values, data, refreshData) => (
        <>
          <FormikField
            primary={true}
            name="blessing"
            instructions={step.instructions}
            title={step.title}
          >
            <div className="mb-4">
              <h3 className="mb-2 text-lg font-bold">Reflection Prompts</h3>
              <p className="mb-2">Use these questions to guide your commitment.</p>
              <ul className="mb-4 list-inside list-disc">
                {step.discussionQuestions.map((question, index) => (
                  <li className={"font-bold"} key={index}>
                    {question}
                  </li>
                ))}
              </ul>
            </div>
            <GoalBlock data={data} plan={plan} />

            <>
              <div className="mb-4 text-sm">Share an Audio or Written Blessing below</div>
              <div className="mb-4">
                <BlessingsPreview
                  deletable={true}
                  data={data}
                  editable={true}
                  theme="blessings"
                  onDelete={refreshData}
                />
              </div>
              {activeType == null ? (
                <div className="flex w-full flex-col gap-4 md:flex-row">
                  <Button
                    bgColor="bg-blessings hover:bg-blessings/90 mb-8"
                    type="button"
                    onClick={() => setIsAudioOpen(true)}
                  >
                    <i className="fas fa-plus-square text-2xl"></i>
                    <span>Record Audio Blessing</span>
                  </Button>
                  <Button
                    bgColor="bg-blessings hover:bg-blessings/90 mb-8"
                    type="button"
                    disabled={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className={`fas ${isUploading ? 'fa-spinner fa-spin' : 'fa-upload'} text-2xl`}></i>
                    <span>{isUploading ? "Uploading..." : "Upload Audio Blessing"}</span>
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".wav,.mp3,.m4a,.aac,.webm,.ogg"
                    className="hidden"
                    onChange={(e) => onFileUpload(e, refreshData)}
                  />
                  <Button
                    bgColor="bg-blessings hover:bg-blessings/90 mb-8"
                    type="button"
                    onClick={() => setActiveType("text")}
                  >
                    <i className="fas fa-plus-square text-2xl"></i>
                    <span>Add Text Blessing</span>
                  </Button>
                </div>
              ) : null}
              {activeType == "text" ? (
                <>
                  <div className="mb-2 flex items-center gap-4">
                    <Field
                      as="textarea"
                      key={"text" + data.text_blessings.length + 1}
                      name="blessing"
                      placeholder="I bless you with..."
                      className="h-32 w-full rounded-md border p-2"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button bgColor="bg-gray-200 hover:bg-gray-300" className="!text-gray-700" type="button" onClick={() => setActiveType(null)}>
                      Cancel
                    </Button>
                    <Button bgColor="bg-blessings hover:bg-blessings/90" type="submit">
                      Add Blessing
                    </Button>
                  </div>
                </>
              ) : null}
            </>
          </FormikField>
          <Modal isOpen={isAudioOpen} setIsOpen={setIsAudioOpen} width="md:max-w-lg" locked={isUploading}>
            <Recorder
              key={recorderKey}
              isUploading={isUploading}
              onReset={() => {
                setRecorderKey(recorderKey + 1)
              }}
              onComplete={blob => onAudioComplete(blob, refreshData)}
            />
          </Modal>
        </>
      )}
    </Step>
  )
}

export default Blessings
